/**
 * The DOM half of the dual-firing contract: a native `CustomEvent` carrying the
 * same envelope as the `postMessage` one, dispatched on the element the checkout
 * is mounted in.
 *
 * A DOM event cannot cross a document boundary, so it is the mechanism for
 * same-document (inline) embeds — DesignMyNight's own pages and a partner
 * mounting `<guest-checkout>` on theirs — while `postMessage` remains the
 * mechanism for a genuine iframe embed. See `docs/analytics.md`.
 */
import type { AnalyticsMessage } from "./types";
import { resolveWidgetElement } from "./widgetElement";

/**
 * Type of the DOM event every tracked envelope is dispatched as.
 *
 * One type for all events, rather than one per event name, so a single
 * `addEventListener` sees the whole funnel; the event name is
 * `detail.event`, exactly as in the `postMessage` envelope. It is namespaced so
 * it cannot collide with a host page's own events.
 */
export const ANALYTICS_DOM_EVENT_TYPE = "collins-guest-checkout:analytics";

/** Container the standalone (non-web-component) build renders into. */
const REACT_ROOT_ID = "root";

/**
 * The element to dispatch on: the r2wc wrapper where the checkout is mounted as
 * a web component, otherwise the React root container, otherwise the document
 * body.
 *
 * r2wc is configured without a shadow root, so the React tree is mounted
 * directly inside the custom element and a bubbling event dispatched on it
 * reaches any listener the host page has on an ancestor, on `document` or on
 * `window`.
 */
export function resolveAnalyticsEventTarget(): Element | null {
  const widget = resolveWidgetElement();
  if (widget) return widget;

  const fallback: Element | null = document.getElementById(REACT_ROOT_ID) ?? document.body;

  return fallback;
}

/**
 * Fires the envelope as a DOM event, so an integrator can consume the funnel
 * with `addEventListener` and no Google Tag Manager.
 *
 * The event bubbles, so a listener on an ancestor, `document` or `window` works,
 * and is `composed`, so it still escapes if the checkout is ever mounted with a
 * shadow root. Nothing is dispatched — and nothing throws — when the document
 * has no element to dispatch on.
 */
export function dispatchAnalyticsDomEvent(message: AnalyticsMessage): void {
  try {
    const target = resolveAnalyticsEventTarget();
    if (!target) return;

    target.dispatchEvent(
      new CustomEvent<AnalyticsMessage>(ANALYTICS_DOM_EVENT_TYPE, {
        detail: message,
        bubbles: true,
        composed: true,
      }),
    );
  } catch (error) {
    console.warn("[Analytics] Failed to dispatch analytics event:", error);
  }
}
